import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../../lib/api/client.js';
import { Icon } from '../../components/ui/Icon.js';
import { Switch } from '../../components/ui/Form.js';
import { TrunkLivePanel } from './TrunkLive.js';
import { invalidateAccountTrunks } from '../../lib/api/hooks-products.js';
import type { Carrier, Trunk } from '@arcnet/contracts';

/**
 * Everything a carrier dictates.
 *
 * The trunk record has held these fields since the schema was written; nothing
 * could change them. A trunk could be created and not configured, which is the
 * worst of both — it looks provisioned and fails on the first call.
 *
 * Grouped by the question being answered rather than by database column, and
 * the number formatting section carries a live preview because it is the
 * setting that most often breaks a carrier integration and the least possible
 * to reason about from the field names alone.
 *
 * The same form CREATES a trunk. The questions are identical either way, and a
 * separate create form would be a second place for them to drift.
 */

const CODECS = ['PCMA', 'PCMU', 'G722', 'G729', 'OPUS'];
const IPV4 = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;

export function TrunkEditor({ trunk, carriers, carrierId, own, onDone }: {
  /** Absent when creating. */
  trunk?: Trunk;
  /** The carriers a new trunk can belong to. */
  carriers?: Carrier[];
  carrierId?: string | undefined;
  /**
   * A customer's OWN trunk (bring your own trunk): saved to that account's
   * own-trunks endpoints, named by its provider rather than one of our
   * carriers, and without inbound rewriting -- which the API refuses for an
   * own trunk, because its carrier's addresses may be shared with others'.
   */
  own?: { tenantId: string };
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const creating = !trunk;
  const base = own ? `/v1/tenants/${own.tenantId}/own-trunks` : '/v1/trunks';
  const [f, setF] = useState({
    provider_name: trunk?.provider_name ?? '',
    carrier_id: trunk?.carrier_id ?? carrierId ?? carriers?.[0]?.id ?? '',
    name: trunk?.name ?? '',
    host: trunk?.host ?? '',
    port: trunk?.port ?? 5060,
    transport: trunk?.transport ?? 'udp',
    use_srv: trunk?.use_srv ?? false,
    direction: trunk?.direction ?? 'both',
    status: trunk?.status ?? 'active',
    auth_mode: trunk?.auth_mode ?? 'ip',
    auth_username: trunk?.auth_username ?? '',
    auth_realm: trunk?.auth_realm ?? '',
    register_expiry_sec: trunk?.register_expiry_sec ?? 3600,
    source_ips: (trunk?.source_ips ?? []).join('\n'),
    outbound_format: trunk?.outbound_format ?? 'e164',
    outbound_prefix: trunk?.outbound_prefix ?? '',
    inbound_strip: trunk?.inbound_strip ?? 0,
    inbound_prepend: trunk?.inbound_prepend ?? '',
    cli_mode: trunk?.cli_mode ?? 'from_and_pai',
    fallback_cli: trunk?.fallback_cli ?? '',
    privacy_mode: trunk?.privacy_mode ?? 'none',
    allowed_codecs: trunk?.allowed_codecs ?? ['PCMA', 'PCMU'],
    dtmf_mode: trunk?.dtmf_mode ?? 'rfc2833',
    t38_enabled: trunk?.t38_enabled ?? false,
    early_media: trunk?.early_media ?? true,
    session_timers: trunk?.session_timers ?? false,
    rel100: trunk?.rel100 ?? false,
    max_concurrent_calls: trunk?.max_concurrent_calls ?? null,
    max_cps: trunk?.max_cps ?? null,
    options_ping_sec: trunk?.options_ping_sec ?? 30,
    failure_threshold: trunk?.failure_threshold ?? 3,
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) =>
    setF((prev) => ({ ...prev, [k]: v }));

  /** Write-only: never pre-filled, and cleared once saved. */
  const [secret, setSecret] = useState('');

  const [tested, setTested] = useState<Array<{ input: string; output: string; note: string | null }> | null>(null);
  const [testing, setTesting] = useState(false);

  const ips = f.source_ips.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);
  const badIps = ips.filter((x) => !IPV4.test(x));
  const digest = f.auth_mode !== 'ip';
  const inbound = f.direction !== 'outbound';

  /** What stops a save, said plainly, before the API has to refuse it. */
  const problem =
    !f.name.trim() ? 'Give the trunk a name.'
    : !f.host.trim() ? 'The carrier host is required.'
    : own && !f.provider_name.trim() ? 'Name the carrier, as on its invoice — Switch Tel, for example.'
    : !own && creating && !f.carrier_id ? 'Choose the carrier this trunk belongs to.'
    : badIps.length > 0 ? `Not an IPv4 address or range: ${badIps.join(', ')}`
    : digest && !f.auth_username.trim() ? 'Digest authentication needs the username the carrier gave you.'
    : digest && !secret && !trunk?.auth_secret_set ? 'Digest authentication needs the password the carrier gave you.'
    : !digest && inbound && ips.length === 0
      ? 'IP authentication needs at least one of the carrier’s source IPs for inbound calls.'
    : f.allowed_codecs.length === 0 ? 'Offer at least one codec.'
    : null;

  const opt = (v: string) => (v.trim() ? v.trim() : undefined);

  const save = useMutation({
    mutationFn: async () => {
      if (!trunk) {
        /**
         * Create takes what a trunk cannot exist without. Whatever the create
         * contract does not accept is applied by a PATCH straight after, so the
         * form means the same thing whether the trunk is new or not.
         */
        const created = await api.post<Trunk>(base, {
          ...(own ? { provider_name: f.provider_name.trim() } : { carrier_id: f.carrier_id }),
          name: f.name.trim(), direction: f.direction,
          host: f.host.trim(), port: f.port, transport: f.transport, use_srv: f.use_srv,
          source_ips: ips, auth_mode: f.auth_mode,
          ...(digest ? {
            auth_username: opt(f.auth_username), auth_realm: opt(f.auth_realm),
            auth_secret: secret, register_expiry_sec: f.register_expiry_sec,
          } : {}),
          max_concurrent_calls: f.max_concurrent_calls ?? undefined,
          max_cps: f.max_cps ?? undefined,
          outbound_format: f.outbound_format, outbound_prefix: opt(f.outbound_prefix),
          ...(own ? {} : { inbound_strip: f.inbound_strip, inbound_prepend: opt(f.inbound_prepend) }),
          cli_mode: f.cli_mode,
          fallback_cli: opt(f.fallback_cli),
          allowed_codecs: f.allowed_codecs, dtmf_mode: f.dtmf_mode, t38_enabled: f.t38_enabled,
        });
        await api.patch(`${base}/${created.id}`, {
          status: f.status, privacy_mode: f.privacy_mode, early_media: f.early_media,
          session_timers: f.session_timers, rel100: f.rel100,
          options_ping_sec: f.options_ping_sec, failure_threshold: f.failure_threshold,
        });
        return;
      }
      await api.patch(`${base}/${trunk.id}`, {
        ...(own ? { provider_name: f.provider_name.trim() } : {}),
        name: f.name.trim(), host: f.host.trim(), port: f.port, transport: f.transport,
        use_srv: f.use_srv, direction: f.direction, status: f.status,
        source_ips: ips,
        auth_mode: f.auth_mode,
        ...(digest ? {
          auth_username: f.auth_username.trim() || null,
          auth_realm: f.auth_realm.trim() || null,
          register_expiry_sec: f.register_expiry_sec,
        } : {}),
        ...(secret ? { auth_secret: secret } : {}),
        outbound_format: f.outbound_format,
        outbound_prefix: f.outbound_prefix || null,
        ...(own ? {} : { inbound_strip: f.inbound_strip, inbound_prepend: f.inbound_prepend || null }),
        cli_mode: f.cli_mode,
        fallback_cli: f.fallback_cli || null,
        privacy_mode: f.privacy_mode,
        allowed_codecs: f.allowed_codecs, dtmf_mode: f.dtmf_mode, t38_enabled: f.t38_enabled,
        early_media: f.early_media, session_timers: f.session_timers, rel100: f.rel100,
        max_concurrent_calls: f.max_concurrent_calls, max_cps: f.max_cps,
        options_ping_sec: f.options_ping_sec, failure_threshold: f.failure_threshold,
      });
    },
    onSuccess: () => {
      setSecret('');
      if (own) invalidateAccountTrunks(qc, own.tenantId);
      void qc.invalidateQueries({ queryKey: ['trunks'] });
      void qc.invalidateQueries({ queryKey: ['carriers'] });
      onDone();
    },
  });

  /**
   * Tests what is SAVED, not what is on screen.
   *
   * The endpoint reads the stored trunk, so a preview of unsaved edits would
   * be a lie. Saying so is better than quietly showing the old answer next to
   * new inputs.
   */
  const runTest = async (): Promise<void> => {
    if (!trunk) return;
    setTesting(true);
    try {
      const r = await api.post<{ results: Array<{ input: string; output: string; note: string | null }> }>(
        `/v1/trunks/${trunk.id}/actions/test-number-format`,
        {
          numbers: ['+27821234567', '+27115550100', '0821234567', '+441632960961', '0800112233'],
          direction: 'outbound',
        },
      );
      setTested(r.results);
    } finally { setTesting(false); }
  };

  const err = save.error instanceof ApiClientError ? save.error : null;

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e) => { e.preventDefault(); if (!problem) save.mutate(); }}>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <h3 className="arc-h2">{trunk ? trunk.name : own ? 'Your own trunk' : 'New trunk'}</h3>
        {trunk && <span className="arc-subtle">{trunk.carrier_name ?? trunk.provider_name}</span>}
        <div className="arc-spacer" />
        <button type="button" className="arc-btn arc-btn-sm" onClick={onDone}>
          <Icon name="close" size={12} /> Close
        </button>
      </div>

      {err && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} /><span>{err.message}</span>
        </div>
      )}

      {/* What the switch sees right now. First, because "is it working?" is the
          question somebody opening a trunk usually has. Compared against the
          realm in the FORM, so choosing the carrier's realm clears the warning
          before saving -- saving is then the obvious last step. */}
      {trunk && (
        <TrunkLivePanel live={trunk.live} authMode={f.auth_mode}
          realm={f.auth_realm.trim() || f.host.trim()}
          onUseRealm={(r) => set('auth_realm', r)}
          trunkId={trunk.id} tenantId={own?.tenantId} />
      )}

      <Section title="Connection" hint="Where the carrier is and how we reach it">
        {own && (
          <Field label="Carrier">
            <input className="arc-input" value={f.provider_name} placeholder="Switch Tel"
              onChange={(e) => set('provider_name', e.target.value)} />
          </Field>
        )}
        {!own && creating && (
          <Field label="Carrier">
            <select className="arc-input" value={f.carrier_id}
              onChange={(e) => set('carrier_id', e.target.value)}>
              {(carriers ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Name">
          <input className="arc-input" value={f.name} placeholder="switchtel-primary"
            onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Host">
          <input className="arc-input arc-mono" value={f.host} placeholder="sip.carrier.co.za"
            onChange={(e) => set('host', e.target.value)} />
        </Field>
        <Field label="Port">
          <input className="arc-input arc-mono" inputMode="numeric" value={f.port}
            onChange={(e) => set('port', Number(e.target.value) || 5060)} />
        </Field>
        <Field label="Transport">
          <select className="arc-input" value={f.transport}
            onChange={(e) => set('transport', e.target.value as typeof f.transport)}>
            <option value="udp">UDP</option><option value="tcp">TCP</option><option value="tls">TLS</option>
          </select>
        </Field>
        <Field label="Direction">
          <select className="arc-input" value={f.direction}
            onChange={(e) => set('direction', e.target.value as typeof f.direction)}>
            <option value="both">Both</option>
            <option value="outbound">Outbound only</option>
            <option value="inbound">Inbound only</option>
          </select>
        </Field>
        <Field label="Status">
          <select className="arc-input" value={f.status}
            onChange={(e) => set('status', e.target.value as typeof f.status)}>
            <option value="active">Active</option>
            {/* Draining stops NEW calls without dropping the ones in progress,
                which is what you want before maintenance. */}
            <option value="draining">Draining</option>
            <option value="disabled">Disabled</option>
          </select>
        </Field>
        <Toggle id="tr-srv" label="Resolve via SRV" checked={f.use_srv}
          onChange={(v) => set('use_srv', v)}
          hint="Let DNS choose the host and port." />
      </Section>

      <Section title="Authentication" hint="How the carrier knows the calls are ours">
        <Field label="Method">
          <select className="arc-input" value={f.auth_mode}
            onChange={(e) => set('auth_mode', e.target.value as typeof f.auth_mode)}>
            <option value="ip">IP address</option>
            <option value="digest">Digest (username and password)</option>
            <option value="register">Digest with registration</option>
          </select>
          <p className="arc-hint">
            {f.auth_mode === 'ip'
              ? 'The carrier trusts our server’s address. There is nothing to log in with.'
              : f.auth_mode === 'digest'
                ? 'We answer the carrier’s password challenge on every outbound call.'
                : 'As digest, and we also register with the carrier so it knows where to send inbound calls.'}
          </p>
        </Field>
        {digest && (
          <>
            <Field label="Username">
              <input className="arc-input arc-mono" autoComplete="off" value={f.auth_username}
                onChange={(e) => set('auth_username', e.target.value)} />
            </Field>
            <Field label="Realm">
              <input className="arc-input arc-mono" autoComplete="off" value={f.auth_realm}
                placeholder={f.host || 'the carrier host'}
                onChange={(e) => set('auth_realm', e.target.value)} />
              <p className="arc-hint">
                As the carrier gave it; blank uses the host. Often NOT the host — Switch Tel’s
                host is sip.switchtel.co.za, its realm switchtel.co.za — and a wrong realm fails
                every login. Once the switch has pinged the carrier, Live status shows its realm.
              </p>
            </Field>
            <Field label="Password">
              <input className="arc-input arc-mono" type="password" autoComplete="new-password"
                value={secret}
                placeholder={trunk?.auth_secret_set ? 'Saved. Leave blank to keep it' : 'Required'}
                onChange={(e) => setSecret(e.target.value)} />
              <p className="arc-hint">Stored encrypted and never shown again.</p>
            </Field>
            {f.auth_mode === 'register' && (
              <Field label="Re-register every (seconds)">
                <input className="arc-input" inputMode="numeric" value={f.register_expiry_sec}
                  onChange={(e) => set('register_expiry_sec', Number(e.target.value) || 3600)} />
              </Field>
            )}
          </>
        )}
      </Section>

      <Section title="Carrier addresses" hint="Where the carrier’s calls come from">
        <Field label="Source IPs, one per line" span>
          <textarea className="arc-input arc-mono" rows={3} value={f.source_ips}
            placeholder={'196.25.1.10\n196.25.2.0/24'}
            onChange={(e) => set('source_ips', e.target.value)} />
          <p className={badIps.length ? 'arc-error' : 'arc-hint'}>
            {badIps.length
              ? `Not an IPv4 address or range: ${badIps.join(', ')}`
              : own
                ? 'Calls from these addresses are recognised as your carrier. Public addresses only, ranges no wider than /24. Ask the carrier for every address its calls can come from.'
                : 'Calls from these addresses are recognised as this carrier: never asked for a password and never banned. Ask the carrier for every address its calls can come from.'}
          </p>
        </Field>
      </Section>

      <Section title="Number formatting"
        hint="What the carrier expects to see — the setting that most often breaks an integration">
        <Field label="Outbound format">
          <select className="arc-input" value={f.outbound_format}
            onChange={(e) => set('outbound_format', e.target.value as typeof f.outbound_format)}>
            <option value="e164">+27821234567 (E.164 with +)</option>
            <option value="e164_no_plus">27821234567 (E.164, no +)</option>
            <option value="national">0821234567 (national)</option>
            <option value="00_prefixed">0027821234567 (00 prefixed)</option>
          </select>
        </Field>
        <Field label="Outbound prefix">
          <input className="arc-input arc-mono" value={f.outbound_prefix}
            placeholder="none" onChange={(e) => set('outbound_prefix', e.target.value)} />
        </Field>
        {own ? (
          /* No inbound rewriting on an own trunk (see the prop): the switch
             takes a number in international, 00 or 0 form instead. */
          <Field label="Incoming numbers">
            <p className="arc-hint" style={{ marginTop: 6 }}>Any format the carrier sends.</p>
          </Field>
        ) : (
          <>
            <Field label="Inbound strip">
              <input className="arc-input arc-mono" inputMode="numeric" value={f.inbound_strip}
                onChange={(e) => set('inbound_strip', Number(e.target.value) || 0)} />
            </Field>
            <Field label="Inbound prepend">
              <input className="arc-input arc-mono" value={f.inbound_prepend}
                placeholder="none" onChange={(e) => set('inbound_prepend', e.target.value)} />
            </Field>
          </>
        )}
      </Section>

      {/* The tester reads the saved trunk through a staff endpoint; an own
          trunk's customer cannot call it, so it is not offered there. */}
      {trunk && !own && (
        <div className="arc-card" style={{ marginBottom: 16, background: 'var(--arc-surface-2)' }}>
          <div className="arc-card-head">
            <h2 className="arc-h2">What the carrier will actually receive</h2>
            <button type="button" className="arc-btn arc-btn-sm" disabled={testing}
              onClick={() => void runTest()}>
              {testing ? 'Testing…' : 'Test formatting'}
            </button>
          </div>
          <div className="arc-card-body">
            {!tested ? (
              <p className="arc-muted" style={{ fontSize: 12.5, margin: 0 }}>
                Runs against the <strong>saved</strong> settings, not the ones above — save first,
                then test. A wrong format fails at the carrier with an opaque 404, which
                is miserable to debug from SIP traces alone.
              </p>
            ) : (
              /* Wrapped like every other table: without it a narrow window makes
                 the PAGE scroll sideways rather than just the table. */
              <div className="arc-table-wrap">
              <table className="arc-table">
                <thead><tr><th>Dialled</th><th>Sent</th><th>Note</th></tr></thead>
                <tbody>
                  {tested.map((r) => (
                    <tr key={r.input}>
                      <td className="arc-mono">{r.input}</td>
                      <td className="arc-mono" style={{ fontWeight: 600 }}>{r.output}</td>
                      <td className="arc-muted">{r.note ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      )}

      <Section title="Caller ID"
        hint="How the calling party is presented and withheld. The switch builds these headers per call.">
        <Field label="Presentation">
          <select className="arc-input" value={f.cli_mode}
            onChange={(e) => set('cli_mode', e.target.value as typeof f.cli_mode)}>
            <option value="from_and_pai">From and P-Asserted-Identity</option>
            <option value="from_only">From only</option>
            <option value="rpid">Remote-Party-ID</option>
          </select>
          <p className="arc-hint">
            Most carriers read P-Asserted-Identity and bill against it. From only sends no
            identity header at all, which also leaves nowhere standard to withhold a number.
          </p>
        </Field>
        <Field label="Fallback CLI">
          <input className="arc-input arc-mono" value={f.fallback_cli}
            placeholder="+27115550100" onChange={(e) => set('fallback_cli', e.target.value)} />
          {f.fallback_cli.trim()
            ? (
              <p className="arc-hint">
                Presented by any extension with no caller ID of its own.
              </p>
            )
            : (
              <p className="arc-error">
                Without this, an extension with no caller ID presents its extension number
                — &ldquo;1001&rdquo; — and the carrier refuses the call.
              </p>
            )}
        </Field>
        <Field label="Withholding">
          <select className="arc-input" value={f.privacy_mode}
            onChange={(e) => set('privacy_mode', e.target.value as typeof f.privacy_mode)}>
            <option value="none">Never withhold</option>
            <option value="id">Privacy: id</option>
            <option value="header">Privacy: header — sent as id</option>
          </select>
          <p className="arc-hint">
            {f.privacy_mode === 'none'
              ? 'An extension set to withhold its number still presents it on this trunk.'
              : 'The number still reaches the carrier — a call with no network CLI is refused'
                + ' — and the carrier withholds it from the called party.'}
          </p>
        </Field>
      </Section>

      <Section title="Media and protocol" hint="What the two ends have to agree on">
        <div className="arc-field" style={{ gridColumn: '1 / -1', margin: 0 }}>
          <label className="arc-label">Codecs offered, in order</label>
          <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {CODECS.map((codec) => {
              const on = f.allowed_codecs.includes(codec);
              return (
                <button key={codec} type="button"
                  className={`arc-btn arc-btn-sm ${on ? 'arc-btn-primary' : 'arc-btn-ghost'}`}
                  onClick={() => set('allowed_codecs', on
                    ? f.allowed_codecs.filter((x) => x !== codec)
                    : [...f.allowed_codecs, codec])}>
                  {codec}
                </button>
              );
            })}
          </div>
          {/* Order is the offer order, and an empty list is not "any codec" --
              it is a trunk that cannot negotiate media at all. */}
          <p className={f.allowed_codecs.length === 0 ? 'arc-error' : 'arc-hint'}>
            {f.allowed_codecs.length === 0
              ? 'No codecs offered — calls on this trunk cannot negotiate media.'
              : `Offered in this order: ${f.allowed_codecs.join(', ')}`}
          </p>
        </div>
        <Field label="DTMF">
          <select className="arc-input" value={f.dtmf_mode}
            onChange={(e) => set('dtmf_mode', e.target.value as typeof f.dtmf_mode)}>
            <option value="rfc2833">RFC 2833 (out of band)</option>
            <option value="info">SIP INFO</option>
            <option value="inband">In band</option>
          </select>
        </Field>
        <Toggle id="tr-t38" label="T.38 fax" checked={f.t38_enabled}
          onChange={(v) => set('t38_enabled', v)} />
        <Toggle id="tr-em" label="Early media" checked={f.early_media}
          onChange={(v) => set('early_media', v)}
          hint="Ringing and network messages before answer." />
        <Toggle id="tr-st" label="Session timers" checked={f.session_timers}
          onChange={(v) => set('session_timers', v)}
          hint="Periodic re-INVITE so a dead call does not bill forever." />
        <Toggle id="tr-100" label="PRACK (100rel)" checked={f.rel100}
          onChange={(v) => set('rel100', v)} />
      </Section>

      <Section title="Capacity and health" hint="What we will send, and when we stop">
        <Field label="Max concurrent calls">
          <input className="arc-input" inputMode="numeric" value={f.max_concurrent_calls ?? ''}
            placeholder="unlimited"
            onChange={(e) => set('max_concurrent_calls', e.target.value ? Number(e.target.value) : null)} />
        </Field>
        <Field label="Max calls per second">
          <input className="arc-input" inputMode="numeric" value={f.max_cps ?? ''}
            placeholder="unlimited"
            onChange={(e) => set('max_cps', e.target.value ? Number(e.target.value) : null)} />
        </Field>
        <Field label="OPTIONS ping (seconds)">
          <input className="arc-input" inputMode="numeric" value={f.options_ping_sec}
            onChange={(e) => set('options_ping_sec', Number(e.target.value) || 0)} />
          <p className="arc-hint">
            How often the switch checks the carrier is there. Any reply counts, even a
            401. 0 disables it; some carriers object.
          </p>
        </Field>
        <Field label="Failures before unhealthy">
          <input className="arc-input" inputMode="numeric" value={f.failure_threshold}
            onChange={(e) => set('failure_threshold', Number(e.target.value) || 1)} />
        </Field>
      </Section>

      <div className="arc-row" style={{ gap: 12 }}>
        <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending || !!problem}>
          {save.isPending ? 'Saving…' : creating ? 'Create trunk' : 'Save trunk'}
        </button>
        {problem && <span className="arc-error" style={{ margin: 0 }}>{problem}</span>}
      </div>
    </form>
  );
}

function Section({ title, hint, children }: {
  title: string; hint: string; children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ marginBottom: 9 }}>
        <h3 className="arc-h2">{title}</h3>
        <p className="arc-muted" style={{ fontSize: 11.5, margin: '2px 0 0' }}>{hint}</p>
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12,
      }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, span, children }: { label: string; span?: boolean; children: React.ReactNode }) {
  return (
    <div className="arc-field" style={{ margin: 0, ...(span ? { gridColumn: '1 / -1' } : {}) }}>
      <label className="arc-label">{label}</label>
      {children}
    </div>
  );
}

function Toggle({ id, label, hint, checked, onChange }: {
  id: string; label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="arc-row" style={{ gap: 10, alignItems: 'flex-start', paddingTop: 18 }}>
      <Switch id={id} checked={checked} onChange={onChange} />
      <label htmlFor={id} style={{ fontSize: 12.5, cursor: 'pointer' }}>
        {label}
        {hint && (
          <span className="arc-muted" style={{ display: 'block', fontSize: 11, marginTop: 2 }}>
            {hint}
          </span>
        )}
      </label>
    </div>
  );
}
